<template>
  <div class="masivo-layout">
    <!-- MAIN -->
    <div class="masivo-main">
      <!-- Volver + título -->
      <div class="mb-6">
        <v-btn text small class="pl-0 mb-3" @click="$router.back()">
          <v-icon left small>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
        <h2 class="masivo-title">Actualización de precios</h2>
        <p class="masivo-subtitle">Editá precios y costos de productos usando una planilla Excel</p>
      </div>

      <!-- SELECTOR PROVEEDOR -->
      <div class="masivo-section mb-6">
        <div class="masivo-section__label">Seleccioná el proveedor</div>
        <v-autocomplete
          v-model="proveedorSeleccionado"
          :items="proveedores"
          item-text="nombre"
          item-value="id"
          placeholder="Buscar proveedor..."
          outlined dense
          return-object
          :loading="loadingProveedores"
          hide-details
          clearable
          @update:search-input="buscarProveedores"
          @change="onProveedorChange"
          @click:clear="onProveedorClear"
          style="max-width: 420px"
        />
      </div>

      <!-- SELECTOR SUCURSAL -->
      <div class="masivo-section mb-6" v-if="sucursales.length > 1">
        <div class="masivo-section__label">Aplicar cambios a</div>
        <v-select
          v-model="sucursalSeleccionadaId"
          :items="opcionesSucursales"
          item-text="nombre"
          item-value="id"
          outlined dense
          hide-details
          style="max-width: 420px"
        />
      </div>

      <!-- ACTION CARDS -->
      <div :class="{ 'masivo-cards--disabled': !proveedorSeleccionado }">

        <!-- Card descarga -->
        <div class="action-card mb-4" @click="proveedorSeleccionado && descargarExcel()">
          <div class="action-card__icon-wrap">
            <v-progress-circular v-if="loadingDescarga" indeterminate size="24" width="2" color="primary" />
            <v-icon v-else color="primary" size="22">mdi-file-download-outline</v-icon>
          </div>
          <div class="action-card__body">
            <div class="action-card__title">Descargá la planilla Excel</div>
            <div class="action-card__sub">
              <template v-if="loadingCount">
                Calculando productos disponibles...
              </template>
              <template v-else-if="proveedorSeleccionado && countProductos !== null">
                Tenés <strong>{{ countProductos }}</strong> producto{{ countProductos !== 1 ? 's' : '' }} disponibles para editar
              </template>
              <template v-else-if="proveedorSeleccionado">
                Descargá el Excel con los productos de <strong>{{ proveedorSeleccionado.nombre }}</strong>
              </template>
              <template v-else>
                Primero seleccioná un proveedor
              </template>
            </div>
          </div>
          <v-icon class="action-card__arrow" size="18">mdi-chevron-right</v-icon>
        </div>

        <!-- Card subida -->
        <div
          class="action-card"
          :class="{ 'action-card--upload-active': archivo }"
          @click="proveedorSeleccionado && $refs.fileInput.$refs.input.click()"
          @dragover.prevent
          @drop.prevent="handleDrop"
        >
          <div class="action-card__icon-wrap">
            <v-icon :color="archivo ? 'success' : 'primary'" size="22">
              {{ archivo ? 'mdi-file-check-outline' : 'mdi-file-upload-outline' }}
            </v-icon>
          </div>
          <div class="action-card__body">
            <div class="action-card__title">Subí la planilla que descargaste</div>
            <div class="action-card__sub">
              <template v-if="archivo">{{ archivo.name }}</template>
              <template v-else>Los cambios pueden tardar unos minutos en verse</template>
            </div>
          </div>
          <v-btn
            v-if="archivo"
            depressed small color="primary"
            class="ml-3 flex-shrink-0"
            :loading="loadingImport"
            @click.stop="subirExcel"
          >
            Importar
          </v-btn>
          <v-icon v-else class="action-card__arrow" size="18">mdi-chevron-right</v-icon>
        </div>

        <v-file-input
          ref="fileInput"
          v-model="archivo"
          accept=".xlsx"
          style="display: none"
        />
      </div>

      <!-- Resultado -->
      <v-alert v-if="resultado" text dense rounded class="mt-6"
        :type="resultado.errores && resultado.errores.length ? 'warning' : 'success'"
      >
        {{ resultado.actualizados }} producto{{ resultado.actualizados !== 1 ? 's' : '' }} actualizado{{ resultado.actualizados !== 1 ? 's' : '' }}
        <span v-if="resultado.errores && resultado.errores.length"> · {{ resultado.errores.length }} con errores</span>
      </v-alert>
    </div>

    <!-- SIDEBAR -->
    <div class="masivo-sidebar">
      <div class="masivo-sidebar__title">Tené en cuenta</div>
      <ul class="masivo-sidebar__list">
        <li>Descargá la planilla cada vez que quieras modificar productos</li>
        <li>Solo subí la última versión que descargaste</li>
        <li>No modifiques el orden ni el nombre de las columnas</li>
        <li>No elimines filas del archivo</li>
        <li>El código externo es obligatorio</li>
        <li>Los cambios pueden tardar unos minutos en reflejarse</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  name: "EdicionExcel",

  data() {
    return {
      proveedorSeleccionado: null,
      proveedores: [],
      sucursalSeleccionadaId: null,
      loadingProveedores: false,
      loadingDescarga: false,
      loadingImport: false,
      loadingCount: false,
      countProductos: null,
      archivo: null,
      resultado: null,
      debounceTimer: null
    };
  },

  computed: {
    ...mapState("sucursales", ["sucursales"]),
    opcionesSucursales() {
      return [
        { id: null, nombre: "Todas las sucursales" },
        ...this.sucursales
      ];
    }
  },

  methods: {
    ...mapActions("sucursales", ["fetch_sucursales"]),

    async buscarProveedores(search) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(async () => {
        this.loadingProveedores = true;
        try {
          const { data } = await this.axios.get("/proveedores/", {
            params: { search }
          });
          this.proveedores = data.results || [];
        } catch {
          this.proveedores = [];
        } finally {
          this.loadingProveedores = false;
        }
      }, 300);
    },

    async onProveedorChange() {
      this.countProductos = null;
      if (!this.proveedorSeleccionado) return;
      this.loadingCount = true;
      try {
        const { data } = await this.axios.get("/productos/psp", {
          params: { proveedor: this.proveedorSeleccionado.id, page_size: 1 }
        });
        this.countProductos = data.count ?? null;
      } catch {
        this.countProductos = null;
      } finally {
        this.loadingCount = false;
      }
    },

    onProveedorClear() {
      this.proveedorSeleccionado = null;
      this.countProductos = null;
    },

    async descargarExcel() {
      if (!this.proveedorSeleccionado) return;
      this.loadingDescarga = true;
      try {
        const params = {};
        if (this.sucursalSeleccionadaId !== null) {
          params.sucursal_id = this.sucursalSeleccionadaId;
        }
        const res = await this.axios.get(
          `/productos/plantilla/${this.proveedorSeleccionado.id}/descargar-lista-precios`,
          { responseType: "blob", params }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "precios.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        this.loadingDescarga = false;
      }
    },

    handleDrop(e) {
      if (!this.proveedorSeleccionado) return;
      const files = e.dataTransfer.files;
      if (files.length) this.archivo = files[0];
    },

    async subirExcel() {
      this.loadingImport = true;
      const formData = new FormData();
      formData.append("file", this.archivo);
      if (this.sucursalSeleccionadaId !== null) {
        formData.append("sucursal_id", this.sucursalSeleccionadaId);
      } else {
        formData.append("todas", "true");
      }
      try {
        const { data } = await this.axios.post(
          `/productos/plantilla/${this.proveedorSeleccionado.id}/importar-lista-precios`,
          formData
        );
        this.resultado = data;
        this.archivo = null;
      } catch {
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          color: "error",
          text: "Error al importar el archivo",
          icon: "mdi-alert",
          timeout: 3000
        });
      } finally {
        this.loadingImport = false;
      }
    }
  },

  async mounted() {
    await this.fetch_sucursales();
    if (this.sucursales.length === 1) {
      this.sucursalSeleccionadaId = this.sucursales[0].id;
    }

    const proveedorId = this.$route.query.proveedor_id;
    if (proveedorId) {
      try {
        const { data } = await this.axios.get(`/proveedores/${proveedorId}`);
        this.proveedores = [data];
        this.proveedorSeleccionado = data;
        this.onProveedorChange();
      } catch {
        this.buscarProveedores("");
      }
    } else {
      this.buscarProveedores("");
    }
  }
};
</script>

<style scoped>
.masivo-layout {
  display: flex;
  min-height: calc(100vh - 64px);
}

.masivo-main {
  flex: 1;
  padding: 32px 40px;
  max-width: 680px;
}

.masivo-sidebar {
  width: 260px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.04);
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  padding: 32px 24px;
}

.masivo-title {
  font-size: 22px;
  font-weight: 700;
  color: #e0e0e0;
  margin-bottom: 6px;
}

.masivo-subtitle {
  font-size: 13px;
  color: #888;
  margin: 0;
}

.masivo-section__label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #888;
  margin-bottom: 10px;
}

.action-card {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 20px 24px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  gap: 16px;
}

.action-card:hover {
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.07);
}

.action-card--upload-active {
  border-color: #2e7d32;
  background: rgba(46, 125, 50, 0.06);
}

.action-card__icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-card__body {
  flex: 1;
  min-width: 0;
}

.action-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #e0e0e0;
  margin-bottom: 4px;
}

.action-card__sub {
  font-size: 12px;
  color: #888;
  line-height: 1.4;
}

.action-card__arrow {
  color: #555;
  flex-shrink: 0;
}

.masivo-cards--disabled .action-card {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.masivo-sidebar__title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #888;
  margin-bottom: 16px;
}

.masivo-sidebar__list {
  padding-left: 16px;
  margin: 0;
}

.masivo-sidebar__list li {
  font-size: 12px;
  color: #777;
  margin-bottom: 12px;
  line-height: 1.5;
}
</style>
